/* eslint-disable node/callback-return -- TODO: fix later */
import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { useShoppingContext } from '@ori/shopping-context';
import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';

import type { MiniBagProps } from '../components/MiniBag';
import type { MiniBagWrapperProps } from '../components/MiniBagWrapper';
import { useApiConfiguration } from '../contexts';
import { logDebug } from '../logger';
import { useBreakpointIndex } from './useBreakpointIndex';

type ToggleMenuCallbackResult = DebouncedFunc<() => void> | undefined;

export const useMiniBag = ({ useOpenedMinibagOnDesktop }: MiniBagWrapperProps) => {
  const breakpointIndex = useBreakpointIndex();
  const { isLoading, isLoaded, shoppingContext } = useShoppingContext();
  const { isApiConfigurationLoading } = useApiConfiguration();
  const { apiConfiguration: { isBasketSharingEnabled = false } = {} } = useApiConfiguration();
  const { isAnonymous = false, shoppingBag: { shoppingBagItems = [] } = {} } = shoppingContext ?? {};
  const items = shoppingBagItems.filter((item) => item.itemType !== 'U');
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [lastCallback, setLastCallback] = useState<ToggleMenuCallbackResult>(undefined);
  const setCallback = useCallback(
    (callback: ToggleMenuCallbackResult) =>
      setLastCallback((oldCallback) => {
        if (oldCallback) {
          logDebug('cancelling old callback ...');
          oldCallback.cancel();
        }
        logDebug('setting up toggle menu callback:', callback);
        callback?.();

        return callback;
      }),
    [],
  );

  logDebug('Rendering ...\n\t current state:', {
    shoppingContext,
    breakpointIndex,
    isVisible,
    lastCallback,
    useOpenedMinibagOnDesktop,
  });

  const isOpenedMinibagOnDesktop = useOpenedMinibagOnDesktop && breakpointIndex === 2;
  const isOtherPageSmallScreen = !useOpenedMinibagOnDesktop && breakpointIndex === 0;

  const debouncedHide = useCallback(
    () =>
      debounce(() => {
        setCallback(undefined);
        setIsVisible(false);
      }, 500),
    [setCallback, setIsVisible],
  );

  const toggleMenuCallback = useCallback<(data: ToggleMenuFlyout.Event) => DebouncedFunc<() => void> | undefined>(
    (data: ToggleMenuFlyout.Event) => {
      logDebug('toggleMenuCallback - event:', data);

      if (isOpenedMinibagOnDesktop || isOtherPageSmallScreen) {
        logDebug('toggleMenuCallback: ignored in this breakpoint');

        return undefined;
      }

      if (data.detail.menuType !== ToggleMenuFlyout.MenuTypes.Minibag) {
        logDebug('toggleMenuCallback: toggle-menu-flyout event for another component, closing ...');
        setIsVisible(false);

        return undefined;
      }

      if (typeof data.detail.shouldOpen === 'undefined') {
        logDebug(
          'toggleMenuCallback: toggle-menu-flyout event without shouldOpen property set,',
          'toggling visibility ...',
        );
        setIsVisible((previousState) => !previousState);

        return undefined;
      }

      if (data.detail.shouldOpen) {
        logDebug('toggleMenuCallback: toggle-menu-flyout event with shouldOpen === true, opening ...');
        setIsVisible(true);

        return undefined;
      }

      logDebug(
        'toggleMenuCallback: toggle-menu-flyout event with shouldOpen === false',
        'closing scheduled after 500 ms ...',
      );

      return debouncedHide();
    },
    [debouncedHide, isOpenedMinibagOnDesktop, isOtherPageSmallScreen, setIsVisible],
  );

  useEffect(() => {
    // always show in eCatalog "desktop" mode
    if (isOpenedMinibagOnDesktop) {
      logDebug('useOpenedMinibagOnDesktop - always shown: opening ...');
      setIsVisible(true);
    }
    // always hide in other pages small screen
    if (isOtherPageSmallScreen) {
      logDebug('other pages and small breakpoint - always hidden: closing ...');
      setIsVisible(false);
    }

    const toggleMenuFlyoutHandler = ((data: ToggleMenuFlyout.Event) =>
      setCallback(toggleMenuCallback(data))) as EventListener;

    window.addEventListener(ToggleMenuFlyout.Name, toggleMenuFlyoutHandler);

    return () => window.removeEventListener(ToggleMenuFlyout.Name, toggleMenuFlyoutHandler);
  }, [
    breakpointIndex,
    isOpenedMinibagOnDesktop,
    isOtherPageSmallScreen,
    lastCallback,
    setCallback,
    setIsVisible,
    toggleMenuCallback,
    useOpenedMinibagOnDesktop,
  ]);

  const handleOverlayClick = () => {
    if (!isOpenedMinibagOnDesktop) {
      setIsVisible(false);
    }
  };

  const onMouseEnter = useCallback(() => {
    if (useOpenedMinibagOnDesktop || isOtherPageSmallScreen) {
      logDebug('mouseenter: ignored in this breakpoint');

      return;
    }
    logDebug('mouseenter');
    setCallback(undefined);
  }, [useOpenedMinibagOnDesktop, isOtherPageSmallScreen, setCallback]);

  const onMouseLeave = useCallback(() => {
    if (useOpenedMinibagOnDesktop || isOtherPageSmallScreen) {
      logDebug('mouseleave: ignored in this breakpoint');

      return;
    }
    logDebug('mouseleave: hiding ...');
    setIsVisible(false);
  }, [useOpenedMinibagOnDesktop, isOtherPageSmallScreen, setIsVisible]);

  const ownerState = { breakpointIndex, isOpenedMinibagOnDesktop, isVisible, useOpenedMinibagOnDesktop };

  const minibagProps: MiniBagProps = {
    breakpointIndex,
    isBasketSharingEnabled: isBasketSharingEnabled && isLoaded && isAnonymous,
    isDataLoaded: isLoaded,
    useOpenedMinibagOnDesktop,
    isLoading: isLoading || isApiConfigurationLoading,
    items,
    onMouseEnter,
    onMouseLeave,
  };

  return {
    isOpenedMinibagOnDesktop,
    isVisible,
    handleOverlayClick,
    minibagProps,
    ownerState,
  };
};
